import { useCallback, useEffect, useState } from 'react';
import { client } from '../../amplify-client';
import { parseJson } from '../../lib/json';

type Rolle = 'Verwalter' | 'Tester' | 'Leser';

type Nutzer = {
  username: string;
  email: string | null;
  anzeigename: string | null;
  status: string; // Cognito-UserStatus, z. B. CONFIRMED, FORCE_CHANGE_PASSWORD
  aktiv: boolean;
  rolle: Rolle | null;
};

const STATUS_LABEL: Record<string, string> = {
  CONFIRMED: 'aktiv',
  FORCE_CHANGE_PASSWORD: 'Einladung ausstehend',
  UNCONFIRMED: 'unbestätigt',
  RESET_REQUIRED: 'Passwort-Reset nötig',
  ARCHIVED: 'archiviert',
  COMPROMISED: 'gesperrt',
};

/**
 * Nutzerverwaltung (Bauphase 7). Ruft direkt die Bauphase-1-Mutationen auf
 * (amplify/functions/nutzer-verwaltung) — bisher nur per AWS-CLI bedienbar,
 * jetzt über eine echte Oberfläche.
 *
 * "Verwalter" ist absichtlich NICHT im Rollen-Dropdown wählbar: laut
 * functional-design.md 3.3 wird diese Rolle nur einmalig direkt im
 * AWS-Backend vergeben, nicht über die Oberfläche.
 */
export function VerwaltungNutzer() {
  const [nutzer, setNutzer] = useState<Nutzer[]>([]);
  const [laedt, setLaedt] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);
  const [aktionUsername, setAktionUsername] = useState<string | null>(null);

  const [email, setEmail] = useState('');
  const [anzeigename, setAnzeigename] = useState('');
  const [neueRolle, setNeueRolle] = useState<'Leser' | 'Tester'>('Tester');
  const [einladen, setEinladen] = useState(false);
  const [einladenFehler, setEinladenFehler] = useState<string | null>(null);
  const [einladenErfolg, setEinladenErfolg] = useState<string | null>(null);

  const laden = useCallback(() => {
    setLaedt(true);
    setFehler(null);
    client.queries
      .nutzerListe()
      .then(({ data, errors }) => {
        if (errors?.length) throw new Error(errors[0].message);
        const liste = parseJson<Nutzer[]>(data) ?? [];
        setNutzer([...liste].sort((a, b) => (a.anzeigename ?? a.email ?? '').localeCompare(b.anzeigename ?? b.email ?? '', 'de')));
      })
      .catch((e) => setFehler(String(e?.message ?? e)))
      .finally(() => setLaedt(false));
  }, []);

  useEffect(() => laden(), [laden]);

  async function rolleAendern(u: Nutzer, rolle: Rolle) {
    setAktionUsername(u.username);
    setFehler(null);
    try {
      const { errors } = await client.mutations.nutzerRolleSetzen({ username: u.username, rolle });
      if (errors?.length) throw new Error(errors[0].message);
      laden();
    } catch (e) {
      setFehler(String(e instanceof Error ? e.message : e));
    } finally {
      setAktionUsername(null);
    }
  }

  async function aktivSchalten(u: Nutzer) {
    setAktionUsername(u.username);
    setFehler(null);
    try {
      const { errors } = await client.mutations.nutzerAktivSetzen({ username: u.username, aktiv: !u.aktiv });
      if (errors?.length) throw new Error(errors[0].message);
      laden();
    } catch (e) {
      setFehler(String(e instanceof Error ? e.message : e));
    } finally {
      setAktionUsername(null);
    }
  }

  async function nutzerEinladen() {
    setEinladen(true);
    setEinladenFehler(null);
    setEinladenErfolg(null);
    try {
      const adresse = email.trim();
      if (!adresse) throw new Error('Bitte eine E-Mail-Adresse angeben.');
      const { data, errors } = await client.mutations.nutzerEinladen({
        email: adresse,
        rolle: neueRolle,
        anzeigename: anzeigename.trim() || undefined,
      });
      if (errors?.length) throw new Error(errors[0].message);
      const ergebnis = parseJson<{ email?: string } | null>(data);
      setEinladenErfolg(`Einladung an ${ergebnis?.email ?? adresse} verschickt.`);
      setEmail('');
      setAnzeigename('');
      laden();
    } catch (e) {
      const nachricht = String(e instanceof Error ? e.message : e);
      setEinladenFehler(
        nachricht.includes('UsernameExistsException') ? 'Für diese E-Mail-Adresse existiert bereits ein Konto.' : nachricht,
      );
    } finally {
      setEinladen(false);
    }
  }

  return (
    <div>
      <section className="karte" style={{ marginBottom: '1.5rem' }}>
        <h2>Neu einladen</h2>
        <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ flex: '1 1 14rem' }}>
            <label htmlFor="einladen-email">E-Mail-Adresse</label>
            <input
              id="einladen-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name@beispiel.de"
            />
          </div>
          <div style={{ flex: '1 1 10rem' }}>
            <label htmlFor="einladen-name">Anzeigename (optional)</label>
            <input id="einladen-name" value={anzeigename} onChange={(e) => setAnzeigename(e.target.value)} />
          </div>
          <div>
            <label htmlFor="einladen-rolle">Rolle</label>
            <select id="einladen-rolle" value={neueRolle} onChange={(e) => setNeueRolle(e.target.value as 'Leser' | 'Tester')}>
              <option value="Leser">Leser</option>
              <option value="Tester">Tester</option>
            </select>
          </div>
          <button className="btn btn-primaer" disabled={einladen} onClick={nutzerEinladen}>
            Einladen
          </button>
        </div>
        {einladenFehler ? (
          <p className="hinweis" style={{ borderColor: 'var(--erdbeere)', marginTop: '0.75rem' }}>
            {einladenFehler}
          </p>
        ) : null}
        {einladenErfolg ? <p className="hinweis" style={{ marginTop: '0.75rem' }}>{einladenErfolg}</p> : null}
      </section>

      <h2>Alle Nutzer</h2>
      {laedt ? <p className="label">Lädt …</p> : null}
      {fehler ? (
        <p className="hinweis" style={{ borderColor: 'var(--erdbeere)' }}>
          {fehler}
        </p>
      ) : null}

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: '0.6rem' }}>
        {nutzer.map((u) => (
          <div
            key={u.username}
            className="karte"
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '1rem',
              flexWrap: 'wrap',
              opacity: aktionUsername === u.username ? 0.6 : 1,
            }}
          >
            <div>
              <div style={{ fontWeight: 600 }}>{u.anzeigename ?? u.email ?? u.username}</div>
              <div style={{ color: 'var(--espresso-70)', fontSize: '0.85rem' }}>
                {u.email} · {STATUS_LABEL[u.status] ?? u.status}
                {!u.aktiv ? ' · deaktiviert' : ''}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
              {u.rolle === 'Verwalter' ? (
                <span className="label">Verwalter</span>
              ) : (
                <select
                  value={u.rolle ?? ''}
                  disabled={aktionUsername === u.username}
                  onChange={(e) => rolleAendern(u, e.target.value as Rolle)}
                >
                  <option value="" disabled>
                    – Rolle wählen –
                  </option>
                  <option value="Leser">Leser</option>
                  <option value="Tester">Tester</option>
                </select>
              )}
              {u.rolle !== 'Verwalter' ? (
                <button
                  className="btn btn-sekundaer"
                  disabled={aktionUsername === u.username}
                  onClick={() => aktivSchalten(u)}
                  style={u.aktiv ? { color: 'var(--erdbeere)' } : undefined}
                >
                  {u.aktiv ? 'Deaktivieren' : 'Aktivieren'}
                </button>
              ) : null}
            </div>
          </div>
        ))}
        {!laedt && nutzer.length === 0 ? <p style={{ color: 'var(--espresso-70)' }}>Keine Nutzer gefunden.</p> : null}
      </div>
    </div>
  );
}
